import { isNotNil } from 'es-toolkit';
import { coerce, object, string, enum as zodEnum, type infer as zodInfer } from 'zod';

export const FooterPrefixSchema = zodEnum(['S', 'L', 'A']);
const TableCellSchema = object({
  column: string().transform(value => {
    const result = FooterPrefixSchema.safeParse(value);
    return result.success ? result.data : parseInt(value, 10);
  }),
  row: coerce.number(),
});

export type TabularNavigationEntry = NavigationEntry &
  zodInfer<typeof TableCellSchema> & {
    titleRaw?: string;
    children: TabularNavigationEntry[];
  };

export interface FooterColum {
  title: string;
  items: TabularNavigationEntry[];
}

export const toTabularItem =
  (seperator: string) =>
  (item: NavigationEntry): TabularNavigationEntry | null => {
    const [config, ...rest] = item.title.split(seperator);
    const title = rest.join(seperator);

    const [column, row] = config.split('.');

    try {
      const cell = TableCellSchema.parse({ column, row });
      return { ...item, titleRaw: item.title, title, ...cell } as TabularNavigationEntry;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

export const toColumns = () => {
  const socials: TabularNavigationEntry = {
    title: 'Socials',
    children: [],
    path: 'socials',
    column: 'S',
    row: -1,
  };
  const legal: TabularNavigationEntry = {
    title: 'Legal',
    children: [],
    path: 'legal',
    column: 'L',
    row: -1,
  };
  const apps: TabularNavigationEntry = {
    title: 'Apps',
    children: [],
    path: 'apps',
    column: 'A',
    row: -1,
  };

  const setParentEntry = (entry: NavigationEntry, item: NavigationEntry) => {
    const keys = ['title', 'icon'] satisfies (keyof NavigationEntry)[];
    keys.forEach(key => (entry[key] = item[key]!));
  };

  return (
    acc: TabularNavigationEntry[],
    item: TabularNavigationEntry,
    index: number,
    items: TabularNavigationEntry[]
  ) => {
    item.isExternalPortalLink ??= item.path.startsWith('https://');

    switch (item.column) {
      case 'L': {
        if (item.row === 0) setParentEntry(legal, item);
        else legal.children.push(item);
        break;
      }

      case 'S': {
        socials.children.push(item);
        break;
      }

      case 'A': {
        if (item.row === 0) setParentEntry(apps, item);
        else apps.children.push(item);
        break;
      }

      default: {
        if (item.row === 0) {
          item.children = item.children
            .concat(acc[item.column]?.children)
            .filter(isNotNil) as TabularNavigationEntry[];
          acc[item.column] = item;
          break;
        }

        if (acc[item.column] == null) {
          acc[item.column] = { children: [] } as never;
        }

        acc[item.column].children.push(item);

        break;
      }
    }

    const isLast = index === items.length - 1;

    return isLast ? acc.concat([socials, legal, apps]) : acc;
  };
};
